import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Notifi, User } from 'src/app/interfaces/interfaces.index';
import { NotificationService } from 'src/app/services/services.index';
import Swal from 'sweetalert2';
import { swalDesition, swalError } from '../config/swalCommon';
import { UserService } from '../../services/user.service';
import { SimpleModalService } from 'ngx-simple-modal';
import { DetailsNotificationComponent } from 'src/app/pages/administrativo/notifications/details-notification/details-notification.component';
import { HubConnectionState } from '@aspnet/signalr';

@Component({
  selector: 'app-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.css']
})
export class UserNotificationsComponent implements OnInit {
  notifications: Notifi[] = [];
  user: User;
  @Output() totalNewNotifications = new EventEmitter<number>();
  @Output() lastNotification = new EventEmitter<string>();
  idLastNotification: number;
  private isMarkingAllRead = false;

  constructor(
    private userService: UserService,
    private nService: NotificationService,
    private simpleModalService: SimpleModalService
  ) {
    // Inicializar usuario de forma segura
    const userFromStorage = sessionStorage.getItem('currentUser');
    this.user = userFromStorage ? JSON.parse(userFromStorage) : null;
  }

  ngOnInit(): void {
    // Validar que el usuario esté autenticado
    if (!this.user && !this.userService.user) {
      console.warn('Usuario no autenticado, no se cargarán notificaciones');
      return;
    }

    // Cargar notificaciones iniciales
    this.getNotifications();
    
    // Conectar SignalR para notificaciones en tiempo real
    this.getSocket();
  }

  hideNotifications = () => document.getElementById('body').click();

  getNotifications = () => {
    const userId = this.user?.id || this.userService.user?.id;
    
    if (!userId) {
      console.warn('No se puede cargar notificaciones: usuario no identificado');
      return;
    }

    this.nService.getNotificationsByUser(userId).subscribe(
      res => {
        this.consult(res);
        this.showAllNotifications();
      },
      err => {
        if (err.status === 401) {
          console.warn('Sesión expirada. Por favor, inicie sesión nuevamente.');
          // El error.interceptor manejará la redirección al login
        } else {
          console.error('Error al cargar notificaciones:', err);
        }
      }
    );
  };

  private consult(res: Notifi[]) {
    this.notifications = res.filter(c => !c.viewed);
    this.totalNewNotifications.emit(res.filter(x => !x.viewed)?.length);
  }

  getSocket() {
    // La conexión SignalR ya está establecida en el navbar constructor
    // Solo nos suscribimos al evento de notificaciones
    if (this.nService.socket) {
      // Verificar estado de conexión
      const socketState = this.nService.socket.state;
      
      if (socketState === HubConnectionState.Connected) {
        console.log(' SignalR conectado correctamente');
      } else {
        console.warn(` SignalR en estado: ${socketState}`);
      }
      
      // Suscribirse al evento de notificaciones
      this.nService.socket.on('getNotifications', (data: Notifi[]) => {
        console.log(' Notificación recibida via SignalR');
        this.consult(data);
      });
    } else {
      console.warn(' Socket SignalR no disponible');
    }
  }

  deleteNotification = (notf: Notifi) => Swal.fire(swalDesition('Eliminar notificación', '¿Desea eliminar esta notificación?'))
    .then(res => {
      if (res.value) {
        // Eliminación optimista de la UI
        const previousNotifications = [...this.notifications];
        this.notifications = this.notifications.filter(n => n.id !== notf.id);
        this.totalNewNotifications.emit(this.notifications.filter(x => !x.viewed).length);
        
        this.nService.deleteNotification(notf).subscribe(
          () => {
            console.log('✅ Notificación eliminada');
          },
          err => {
            // Si falla, restaurar
            if (err.status === 401) {
              console.warn('Sesión expirada');
              this.notifications = previousNotifications;
              this.totalNewNotifications.emit(previousNotifications.filter(x => !x.viewed).length);
            } else {
              console.error('Error al eliminar notificación:', err);
              this.getNotifications(); // Recargar para sincronizar
              this.handleError(err.error?err.error:err);
            }
          }
        );
      }
    })

  toggleDiv(name: string, item: Notifi) {
    const element = document.getElementById(name);
    if (element.getAttribute('style')) {
      element.removeAttribute('style');
      if (!item.viewed) { this.markAsRead(item); }
    } else { element.setAttribute('style', 'display:block !important'); }
  }

  markAsRead(item: Notifi) {
    // Actualización optimista de la UI: eliminar de la lista de no leídas
    const previousNotifications = [...this.notifications];
    this.notifications = this.notifications.filter(n => n.id !== item.id);
    this.totalNewNotifications.emit(this.notifications.length);
    
    // Actualizar en servidor
    item.viewed = true;
    this.nService.updateNotification(item).subscribe(
      () => {
        console.log('✅ Notificación marcada como leída');
      },
      err => {
        // Si falla, restaurar la notificación
        if (err.status === 401) {
          console.warn('Sesión expirada');
          this.notifications = previousNotifications;
          this.totalNewNotifications.emit(previousNotifications.length);
        } else {
          console.error('Error al marcar notificación como leída:', err);
          this.getNotifications(); // Recargar para sincronizar
          this.handleError(err.error?err.error:err);
        }
      }
    );
  }

  handleError = (err) => Swal.fire(swalError('Ha ocurrido un error', err));

  markAllRead = () => {
    // Prevenir múltiples ejecuciones simultáneas
    if (this.isMarkingAllRead) {
      return;
    }

    const unreadNotifications = this.notifications.filter(x => !x.viewed);
    
    if (unreadNotifications.length === 0) {
      return;
    }

    // Validar que el usuario existe (de cualquier fuente)
    const userId = this.user?.id || this.userService.user?.id;
    
    if (!userId) {
      console.error('Usuario no encontrado');
      Swal.fire({
        icon: 'error',
        title: 'Sesión expirada',
        text: 'Por favor, inicie sesión nuevamente'
      });
      return;
    }

    this.isMarkingAllRead = true;

    // Actualización optimista: limpiar la lista de notificaciones no leídas
    const previousNotifications = [...this.notifications];
    this.notifications = [];
    this.totalNewNotifications.emit(0);

    // Marcar todas como leídas en el servidor con una sola petición
    this.nService.markAllAsRead(userId).subscribe(
      () => {
        console.log('✅ Todas las notificaciones marcadas como leídas');
        this.isMarkingAllRead = false;
      },
      err => {
        // Si falla, restaurar las notificaciones
        console.error('Error al marcar notificaciones como leídas:', err);
        this.notifications = previousNotifications;
        this.totalNewNotifications.emit(previousNotifications.filter(n => !n.viewed).length);
        this.isMarkingAllRead = false;
        
        if (err.status === 401) {
          Swal.fire({
            icon: 'warning',
            title: 'Sesión expirada',
            text: 'Por favor, inicie sesión nuevamente'
          });
        } else {
          this.handleError(err.error?err.error:err);
        }
      }
    );
  };

  showAllNotifications() {
    if (this.notifications.length > 0 && this.idLastNotification !== this.notifications[0]?.id) {
      this.idLastNotification = this.notifications[0]?.id;
      this.lastNotification.emit(this.notifications[0]?.title);
    }
  }

  showNotification(item: Notifi): void {
    this.simpleModalService.addModal(DetailsNotificationComponent, {
      title: item.title,
      message: item.message,
      type: item.type,
      id: item.id,
      date: item.createdAt,
      petitionId: item.petitionId,
      docName: item.docName,
      userId: item.userId
    });
  }
}
